import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { PARAMS } from '../sim/params';

/**
 * The creature: ~300 tris of merged primitives, flat-shaded, vertex-colored.
 * All animation is vertex-shader math driven by per-instance attributes —
 * no rig, no bake, no downloads. One InstancedMesh (+ one inverted-hull
 * outline mesh sharing its buffers) renders the whole population.
 */

// part ids for the aPart vertex attribute
const PART_BODY = 0;
const PART_HEAD = 1;
const PART_EAR_L = 2;
const PART_EAR_R = 3;
const PART_FEET = 4;
const PART_BEAK = 5;

const BODY_YELLOW = '#F2D024';
const FEET_BROWN = '#CE9418';
const BEAK_ORANGE = '#E0761E';
const EYE_DARK = '#241C12';
const OUTLINE_BROWN = '#262014'; // neutral dark ink — reads on skin, onesie, and hair alike

/** deterministic per-face jitter — the fur/mottle grain on flat shading */
function faceHash(face: number, salt: number): number {
  let h = Math.imul(face + 1, 0x27d4eb2d) ^ Math.imul(salt + 1, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  return ((h ^ (h >>> 13)) >>> 0) / 4294967296;
}

function tagPart(geoIn: THREE.BufferGeometry, part: number, color: string): THREE.BufferGeometry {
  // icosahedrons come non-indexed, cones/boxes indexed — mergeGeometries
  // requires all-or-none, and we want unshared verts for flat shading anyway
  let geo = geoIn;
  if (geo.index !== null) {
    geo = geoIn.toNonIndexed();
    geoIn.dispose();
  }
  const count = geo.getAttribute('position').count;
  const c = new THREE.Color(color);
  const colors = new Float32Array(count * 3);
  const parts = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    // fur-speckle: each facet shifts a few percent, with rare darker flecks
    const face = (i / 3) | 0;
    let f = 1 + (faceHash(face, part) - 0.5) * 0.09;
    if (faceHash(face, part + 97) < 0.09) f *= 0.93;
    colors[i * 3] = c.r * f;
    colors[i * 3 + 1] = c.g * f;
    colors[i * 3 + 2] = c.b * f;
    parts[i] = part;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.setAttribute('aPart', new THREE.BufferAttribute(parts, 1));
  return geo;
}

export function buildCreatureGeometry(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];

  const body = new THREE.IcosahedronGeometry(0.44, 1);
  body.scale(1.0, 1.12, 0.92);
  body.translate(0, 0.58, 0);
  parts.push(tagPart(body, PART_BODY, BODY_YELLOW));

  const head = new THREE.IcosahedronGeometry(0.32, 1);
  head.translate(0, 1.18, 0.05);
  parts.push(tagPart(head, PART_HEAD, BODY_YELLOW));

  const earL = new THREE.ConeGeometry(0.1, 0.42, 5);
  earL.rotateZ(0.24);
  earL.translate(-0.16, 1.55, 0);
  parts.push(tagPart(earL, PART_EAR_L, BODY_YELLOW));

  const earR = new THREE.ConeGeometry(0.1, 0.42, 5);
  earR.rotateZ(-0.24);
  earR.translate(0.16, 1.55, 0);
  parts.push(tagPart(earR, PART_EAR_R, BODY_YELLOW));

  const footL = new THREE.BoxGeometry(0.17, 0.11, 0.27);
  footL.translate(-0.15, 0.055, 0.05);
  parts.push(tagPart(footL, PART_FEET, FEET_BROWN));

  const footR = new THREE.BoxGeometry(0.17, 0.11, 0.27);
  footR.translate(0.15, 0.055, 0.05);
  parts.push(tagPart(footR, PART_FEET, FEET_BROWN));

  const beak = new THREE.ConeGeometry(0.07, 0.18, 4);
  beak.rotateX(Math.PI / 2);
  beak.translate(0, 1.12, 0.36);
  parts.push(tagPart(beak, PART_BEAK, BEAK_ORANGE));

  // big eyes — move with the head
  const eyeL = new THREE.IcosahedronGeometry(0.07, 0);
  eyeL.translate(-0.13, 1.26, 0.26);
  parts.push(tagPart(eyeL, PART_HEAD, EYE_DARK));

  const eyeR = new THREE.IcosahedronGeometry(0.07, 0);
  eyeR.translate(0.13, 1.26, 0.26);
  parts.push(tagPart(eyeR, PART_HEAD, EYE_DARK));

  const merged = mergeGeometries(parts, false);
  for (const g of parts) g.dispose();
  merged.deleteAttribute('uv'); // untextured; keeps the shader surface minimal
  merged.computeVertexNormals(); // unshared verts -> true flat facets
  return merged;
}

/**
 * The voxel Songlet (default look, per the lil.png reference): green spiky
 * voxel hair, pale-yellow head with big block eyes and brown ear patches,
 * light-blue onesie with stubby arms, yellow feet on black soles. Built
 * entirely from boxes (~32 cubes, ~380 tris). Same aPart ids as v1 so every
 * animation — hop, eat-peck, sleep-droop, moss-ball — works unchanged.
 */
export function buildCreatureGeometryV2(): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const SKIN = '#EFD98C';
  const ONESIE = '#A5C9E8';
  const HAIR = '#4BBE3A';
  const PATCH = '#7A4E22';
  const SOLE = '#1A140D';
  const NOSE = '#2E2114';

  const box = (
    w: number,
    h: number,
    d: number,
    x: number,
    y: number,
    z: number,
    part: number,
    color: string,
    rotZ = 0,
  ): void => {
    const geo = new THREE.BoxGeometry(w, h, d);
    if (rotZ !== 0) geo.rotateZ(rotZ);
    geo.translate(x, y, z);
    parts.push(tagPart(geo, part, color));
  };

  // feet on soles, toes forward
  for (const s of [-1, 1] as const) {
    box(0.26, 0.07, 0.36, s * 0.17, 0.035, 0.1, PART_FEET, SOLE);
    box(0.24, 0.13, 0.32, s * 0.17, 0.135, 0.1, PART_FEET, SKIN);
  }

  // onesie: two stacked boxes, flared at the bottom like a little gown
  box(0.66, 0.36, 0.52, 0, 0.38, 0, PART_BODY, ONESIE);
  box(0.56, 0.34, 0.46, 0, 0.68, 0, PART_BODY, ONESIE);

  // stubby arms: sleeve + bare hand, angled just off horizontal
  for (const s of [-1, 1] as const) {
    box(0.32, 0.15, 0.17, s * 0.43, 0.74, 0, PART_BODY, ONESIE, s * -0.28);
    box(0.15, 0.15, 0.15, s * 0.6, 0.68, 0, PART_BODY, SKIN, s * -0.28);
  }

  // the big head
  box(0.92, 0.66, 0.76, 0, 1.17, 0, PART_HEAD, SKIN);

  // face: tall block eyes set wide, small dark nose (tagged BEAK so it
  // pecks while eating)
  box(0.13, 0.2, 0.05, -0.21, 1.22, 0.385, PART_HEAD, EYE_DARK);
  box(0.13, 0.2, 0.05, 0.21, 1.22, 0.385, PART_HEAD, EYE_DARK);
  box(0.12, 0.09, 0.09, 0, 1.04, 0.4, PART_BEAK, NOSE);

  // ear nubs with brown inner patches
  for (const s of [-1, 1] as const) {
    const part = s < 0 ? PART_EAR_L : PART_EAR_R;
    box(0.12, 0.3, 0.36, s * 0.51, 1.12, 0, part, SKIN);
    box(0.05, 0.16, 0.2, s * 0.555, 1.12, 0.03, part, PATCH);
  }

  // the green mop: tall, wild voxel tufts standing on the crown, a fringe
  // overhanging the brow, and a little spill down the back
  const HAIR_SPOTS: [number, number][] = [
    [-0.33, -0.2], [-0.11, -0.26], [0.13, -0.22], [0.33, -0.16],
    [-0.31, 0.04], [-0.07, 0.0], [0.15, 0.06], [0.34, 0.1],
    [-0.2, 0.26], [0.04, 0.28], [0.27, 0.24], [0.0, -0.12],
  ];
  HAIR_SPOTS.forEach(([hx, hz], i) => {
    const size = 0.16 + faceHash(i, 5) * 0.08;
    const height = 0.22 + faceHash(i, 9) * 0.3;
    box(size, height, size, hx, 1.5 + height / 2 - 0.02, hz, PART_HEAD, HAIR);
  });
  // fringe overhanging the front hairline
  box(0.2, 0.18, 0.16, -0.18, 1.48, 0.34, PART_HEAD, HAIR);
  box(0.17, 0.24, 0.15, 0.1, 1.5, 0.36, PART_HEAD, HAIR);
  // back spill
  box(0.18, 0.24, 0.14, -0.16, 1.4, -0.38, PART_HEAD, HAIR);
  box(0.16, 0.3, 0.13, 0.12, 1.36, -0.39, PART_HEAD, HAIR);

  const merged = mergeGeometries(parts, false);
  for (const g of parts) g.dispose();
  merged.deleteAttribute('uv');
  merged.computeVertexNormals();
  return merged;
}

/**
 * Shared vertex-animation chunk, injected into both the Lambert material and
 * the outline material so the hull tracks every motion exactly.
 * Anim ids: 0 idle (breathe + ear twitch), 1 hop.
 */
const ANIM_DECLS = /* glsl */ `
uniform float uTime;
attribute float aPart;
attribute float iAnim;
attribute float iPhase;
attribute float iEmotion;
attribute float iScale;
attribute float iSeed;
attribute float iHue;
varying float vEmotion;
varying float vAnim;
varying float vHue;
`;

const ANIM_BODY = /* glsl */ `
{
  vEmotion = iEmotion;
  vAnim = iAnim;
  vHue = iHue;
  float t = uTime + iPhase * 6.2831853;
  vec3 p = transformed;
  float sad = 1.0 - clamp(iEmotion, 0.0, 1.0);
  float slump = 1.0 - 0.16 * sad;
  if (iAnim < 0.5) {
    // idle: breathe, rare ear flicks; sad creatures droop
    float breathe = 1.0 + 0.035 * sin(t * 1.8 + iSeed * 6.0);
    p.y *= breathe * slump;
    if (aPart > 1.5 && aPart < 3.5) {
      float side = (aPart - 2.5) * 2.0;
      float twitch = max(0.0, sin(t * 3.4 + iSeed * 17.0) - 0.86) * 3.0;
      p.x += side * (twitch * 0.05 - sad * 0.06) * p.y;
      p.y -= sad * 0.10 * max(0.0, p.y - 1.3);
    }
  } else if (iAnim < 1.5) {
    // hop: anticipation squash -> airborne arc, head leads
    float c = sin(t * 3.2 + iSeed * 2.0);
    float air = max(0.0, c);
    float squash = 1.0 - 0.20 * max(0.0, -c);
    p.y *= squash * slump;
    p.xz *= 1.0 + 0.14 * max(0.0, -c);
    p.y += air * air * 0.30;
    if (aPart > 0.5 && aPart < 1.5) p.z += air * 0.05;
  } else if (iAnim < 2.5) {
    // eat: head + beak peck toward the ground
    float peck = abs(sin(t * 3.6));
    if (aPart > 0.5 && aPart < 1.5 || aPart > 4.5) {
      p.y -= peck * 0.14;
      p.z += peck * 0.10;
    }
    p.y *= slump;
  } else if (iAnim < 4.5) {
    // sleep: settled squash, slow deep breathing, drooped ears
    float breathe = 1.0 + 0.05 * sin(t * 0.9);
    p.y *= 0.82 * breathe;
    if (aPart > 1.5 && aPart < 3.5) {
      float side = (aPart - 2.5) * 2.0;
      p.x += side * 0.10 * p.y;
      p.y -= 0.12 * max(0.0, p.y - 1.1);
    }
  } else {
    // dormant: curled into a moss ball (paused life, not death).
    // while dormant, iEmotion carries scrub-wake progress: the curl loosens
    // and the sleeper stirs under your sponge
    float wake = clamp(iEmotion, 0.0, 1.0);
    vec3 center = vec3(0.0, 0.55, 0.0);
    vec3 dir = p - center;
    float len = max(length(dir), 1e-4);
    vec3 ball = center + (dir / len) * 0.42;
    // voxel bodies need a tight pull or the corners read as rubble
    p = mix(p, ball, 0.88 * (1.0 - 0.6 * wake));
    p.y *= mix(0.72, 0.92, wake);
    p.y += 0.02 * sin(t * 0.5); // the faintest sign of life
    p.x += wake * 0.03 * sin(t * 11.0); // stirring
  }
  transformed = p * iScale;
}
`;

export interface InstanceState {
  x: number;
  y: number;
  z: number;
  heading: number; // radians around +Y; creature faces +Z at heading 0
  anim: number;
  emotion: number; // 0 glum .. 1 thriving
  scale: number; // 0.6 juvenile .. 1.2 adult
  seed: number; // per-creature variation, stable
  phase: number; // anim de-sync, stable
  hue: number; // 0..1 lineage tint gene
}

export class CreatureRenderer {
  readonly group = new THREE.Group();
  readonly mesh: THREE.InstancedMesh;
  private readonly outline: THREE.InstancedMesh;
  private readonly shadow: THREE.InstancedMesh;
  private readonly uTime = { value: 0 };

  private readonly iAnim: THREE.InstancedBufferAttribute;
  private readonly iPhase: THREE.InstancedBufferAttribute;
  private readonly iEmotion: THREE.InstancedBufferAttribute;
  private readonly iScale: THREE.InstancedBufferAttribute;
  private readonly iSeed: THREE.InstancedBufferAttribute;
  private readonly iHue: THREE.InstancedBufferAttribute;

  private readonly tmpMat = new THREE.Matrix4();
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly tmpPos = new THREE.Vector3();
  private readonly tmpScale = new THREE.Vector3(1, 1, 1);
  private readonly yAxis = new THREE.Vector3(0, 1, 0);

  constructor(capacity: number = PARAMS.INSTANCE_CAP, cute = false) {
    const geometry = cute ? buildCreatureGeometryV2() : buildCreatureGeometry();

    this.iAnim = new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1);
    this.iPhase = new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1);
    this.iEmotion = new THREE.InstancedBufferAttribute(new Float32Array(capacity).fill(1), 1);
    this.iScale = new THREE.InstancedBufferAttribute(new Float32Array(capacity).fill(1), 1);
    this.iSeed = new THREE.InstancedBufferAttribute(new Float32Array(capacity), 1);
    this.iHue = new THREE.InstancedBufferAttribute(new Float32Array(capacity).fill(0.5), 1);
    geometry.setAttribute('iAnim', this.iAnim);
    geometry.setAttribute('iPhase', this.iPhase);
    geometry.setAttribute('iEmotion', this.iEmotion);
    geometry.setAttribute('iScale', this.iScale);
    geometry.setAttribute('iSeed', this.iSeed);
    geometry.setAttribute('iHue', this.iHue);

    const material = new THREE.MeshLambertMaterial({ vertexColors: true });
    material.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = this.uTime;
      shader.vertexShader =
        ANIM_DECLS +
        shader.vertexShader.replace(
          '#include <begin_vertex>',
          '#include <begin_vertex>\n' + ANIM_BODY,
        );
      shader.fragmentShader =
        'varying float vEmotion;\nvarying float vAnim;\nvarying float vHue;\n' +
        shader.fragmentShader.replace(
          '#include <color_fragment>',
          /* glsl */ `#include <color_fragment>
          {
            // lineage tint: gene shifts the yellow toward orange or lime
            float shift = (vHue - 0.5) * 0.55;
            float yellowness = smoothstep(0.15, 0.5, diffuseColor.r - diffuseColor.b);
            diffuseColor.rgb *= mix(vec3(1.0), vec3(1.0 + shift * 0.35, 1.0 - abs(shift) * 0.06, 1.0 - shift * 0.5), yellowness);
            float e = clamp(vEmotion, 0.0, 1.0);
            vec3 grey = vec3(dot(diffuseColor.rgb, vec3(0.299, 0.587, 0.114)));
            diffuseColor.rgb = mix(grey * vec3(0.80, 0.86, 0.80), diffuseColor.rgb, 0.35 + 0.65 * e);
            if (vAnim > 4.5) {
              // dormant moss ball: overgrowth dissolves away as scrubbing
              // progresses (vEmotion = wake progress while dormant)
              float wake = clamp(vEmotion, 0.0, 1.0);
              diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.36, 0.46, 0.30), 0.85 * (1.0 - wake));
            }
          }`,
        );
    };
    material.customProgramCacheKey = () => 'creature';

    this.mesh = new THREE.InstancedMesh(geometry, material, capacity);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    this.mesh.name = 'creatures';

    // inverted-hull outline: dark silhouette for the 1994 look and for
    // yellow-on-green contrast (accessibility)
    const outlineMaterial = new THREE.MeshBasicMaterial({
      color: OUTLINE_BROWN,
      side: THREE.BackSide,
    });
    outlineMaterial.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = this.uTime;
      shader.vertexShader =
        ANIM_DECLS +
        shader.vertexShader.replace(
          '#include <begin_vertex>',
          '#include <begin_vertex>\n' +
            ANIM_BODY +
            /* glsl */ `
          {
            vec3 dir = normalize(transformed / max(iScale, 0.001) - vec3(0.0, 0.72, 0.0));
            transformed += dir * 0.05 * iScale;
          }`,
        );
    };
    outlineMaterial.customProgramCacheKey = () => 'creature-outline';

    this.outline = new THREE.InstancedMesh(geometry, outlineMaterial, capacity);
    this.outline.count = 0;
    this.outline.frustumCulled = false;
    this.outline.name = 'creatures-outline';
    // share the transform buffer — one write drives both meshes
    this.outline.instanceMatrix = this.mesh.instanceMatrix;

    // blob shadows: instanced flat discs sharing transforms + anim attrs;
    // the shader shrinks the disc while the creature is airborne
    const shadowGeometry = new THREE.CircleGeometry(0.58, 8);
    shadowGeometry.rotateX(-Math.PI / 2);
    shadowGeometry.translate(0, 0.045, 0);
    shadowGeometry.setAttribute('iAnim', this.iAnim);
    shadowGeometry.setAttribute('iPhase', this.iPhase);
    shadowGeometry.setAttribute('iScale', this.iScale);
    shadowGeometry.setAttribute('iSeed', this.iSeed);
    const shadowMaterial = new THREE.MeshBasicMaterial({
      color: '#1E3314',
      transparent: true,
      opacity: 0.35,
      depthWrite: false,
    });
    shadowMaterial.onBeforeCompile = (shader) => {
      shader.uniforms.uTime = this.uTime;
      shader.vertexShader =
        /* glsl */ `
        uniform float uTime;
        attribute float iAnim;
        attribute float iPhase;
        attribute float iScale;
        attribute float iSeed;
        ` +
        shader.vertexShader.replace(
          '#include <begin_vertex>',
          /* glsl */ `#include <begin_vertex>
          {
            float t = uTime + iPhase * 6.2831853;
            float air = iAnim > 0.5 ? max(0.0, sin(t * 3.2 + iSeed * 2.0)) : 0.0;
            transformed.xz *= iScale * (1.0 - 0.3 * air * air);
          }`,
        );
    };
    shadowMaterial.customProgramCacheKey = () => 'creature-shadow';
    this.shadow = new THREE.InstancedMesh(shadowGeometry, shadowMaterial, capacity);
    this.shadow.count = 0;
    this.shadow.frustumCulled = false;
    this.shadow.name = 'creatures-shadow';
    this.shadow.instanceMatrix = this.mesh.instanceMatrix;

    this.group.add(this.shadow, this.outline, this.mesh);
  }

  setCount(n: number): void {
    this.mesh.count = n;
    this.outline.count = n;
    this.shadow.count = n;
  }

  setInstance(i: number, s: InstanceState): void {
    this.tmpQuat.setFromAxisAngle(this.yAxis, s.heading);
    this.tmpPos.set(s.x, s.y, s.z);
    this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
    this.mesh.setMatrixAt(i, this.tmpMat);
    this.iAnim.setX(i, s.anim);
    this.iPhase.setX(i, s.phase);
    this.iEmotion.setX(i, s.emotion);
    this.iScale.setX(i, s.scale);
    this.iSeed.setX(i, s.seed);
    this.iHue.setX(i, s.hue);
  }

  setAnim(i: number, anim: number): void {
    if (this.iAnim.getX(i) !== anim) {
      this.iAnim.setX(i, anim);
      this.iAnim.needsUpdate = true;
    }
  }

  /** Flag GPU buffers after a batch of setInstance calls. */
  commit(): void {
    this.mesh.instanceMatrix.needsUpdate = true;
    this.iAnim.needsUpdate = true;
    this.iPhase.needsUpdate = true;
    this.iEmotion.needsUpdate = true;
    this.iScale.needsUpdate = true;
    this.iSeed.needsUpdate = true;
    this.iHue.needsUpdate = true;
  }

  update(dt: number): void {
    this.uTime.value += dt; // local clock: pauses with the tab, no Date.now
  }
}
